import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { Refresh } from '@mui/icons-material';
import { Button } from '@mui/material';
import { EditorState, LexicalEditor } from 'lexical';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import awsRum from '../../../awsRum';
import defaultRichText from '../../../utils/defaultRichText';
import { SharedHistoryContext, useSharedHistoryContext } from '../context/SharedHistoryContext';
import PlaygroundNodes from '../nodes/PlaygroundNodes';
import TableCellNodes from '../nodes/TableCellNodes';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import CardLinkPlugin from '../plugins/CardLinkPlugin';
import CollapsiblePlugin from '../plugins/CollapsiblePlugin';
import ComponentPickerPlugin from '../plugins/ComponentPickerPlugin';
import DecisionTreeLinkPlugin from '../plugins/DecisionTreeLinkPlugin';
import DragDropPaste from '../plugins/DragDropPastePlugin';
import DraggableBlockPlugin from '../plugins/DraggableBlockPlugin';
import EmojiPickerPlugin from '../plugins/EmojiPickerPlugin';
import EmojisPlugin from '../plugins/EmojisPlugin';
import FloatingLinkEditor from '../plugins/FloatingEditors/FloatingLinkEditor';
import FloatingTableCellEditor from '../plugins/FloatingEditors/FloatingTableCellEditor';
import FormulaNodePlugin from '../plugins/FormulaPlugin';
import ImagesPlugin from '../plugins/ImagesPlugin';
import LinkPlugin from '../plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from '../plugins/ListMaxIndentLevelPlugin';
import TableCellResizer from '../plugins/TableCellResizer';
import { TablePlugin as NewTablePlugin, TableContext } from '../plugins/TablePlugin';
import ToolbarPlugin from '../plugins/ToolbarPlugin';
import PlaygroundEditorTheme from '../themes/PlaygroundEditorTheme';
import ContentEditable from '../ui/ContentEditable';
import Placeholder from '../ui/Placeholder';
import './index.css';
import StyledBox from './styles';

interface WysiwygProps {
  initValue: string;
  placeholder: string;
  // eslint-disable-next-line react/no-unused-prop-types
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
  onlyText?: boolean;
}

function onError(error: Error) {
  awsRum?.recordError(error);
  console.error(error);
}

function InnerWysiwyg(props: Omit<Omit<WysiwygProps, 'initValue'>, 'id'>) {
  const {
    placeholder, onChange, disabled,
  } = props;
  const { historyState } = useSharedHistoryContext();
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(false);
  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };
  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport = window.matchMedia('(max-width: 1025px)').matches;
      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  const cellEditorConfig = {
    namespace: 'CogNeed',
    nodes: [...TableCellNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: PlaygroundEditorTheme,
  };
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (disabled) {
    return (
      <RichTextPlugin
        contentEditable={(
          <ContentEditable />
        )}
        placeholder={<Placeholder>{placeholder}</Placeholder>}
        ErrorBoundary={LexicalErrorBoundary}
      />
    );
  }

  return (
    <>
      <ToolbarPlugin />
      <div className="editor-container">
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState: EditorState, fe: LexicalEditor, tags: Set<string>) => {
            if (tags.has('focus')) return;
            onChange(JSON.stringify(editorState.toJSON()));
          }}
        />
        <DragDropPaste />
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        <EmojiPickerPlugin />
        <EmojisPlugin />
        <HashtagPlugin />
        <AutoLinkPlugin />
        <HistoryPlugin externalHistoryState={historyState} />
        <RichTextPlugin
          contentEditable={(
            <div className="editor-scroller">
              <div className="editor" ref={onRef}>
                <ContentEditable />
              </div>
            </div>
          )}
          placeholder={<Placeholder>{placeholder}</Placeholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TablePlugin
          hasCellMerge
          hasCellBackgroundColor
        />
        <TableCellResizer />
        <NewTablePlugin cellEditorConfig={cellEditorConfig}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="TableNode__contentEditable" />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ImagesPlugin captionsEnabled={false} />
          <LinkPlugin />
        </NewTablePlugin>
        <ImagesPlugin />
        <DecisionTreeLinkPlugin />
        <CardLinkPlugin />
        <LinkPlugin />
        <HorizontalRulePlugin />
        <TabIndentationPlugin />
        <FormulaNodePlugin />
        <CollapsiblePlugin />
        {floatingAnchorElem && !isSmallWidthViewport && (
          <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
        )}
        <FloatingLinkEditor />
        <FloatingTableCellEditor />
      </div>
    </>
  );
}

export default function Wysiwyg(props: WysiwygProps) {
  const { initValue, disabled } = props;
  const [refresh, setRefresh] = useState<boolean>(false);
  const { onChange, ...others } = props;
  const { t } = useTranslation('wysiwyg');
  const initialConfig: InitialConfigType = {
    editorState: initValue,
    namespace: 'WysiwygRichText',
    nodes: [...PlaygroundNodes],
    onError,
    theme: PlaygroundEditorTheme,
    editable: !disabled,
  };

  if (refresh) {
    return (
      <StyledBox>
        <Button onClick={() => { setRefresh(false); }} variant="contained" color="primary">
          <Refresh />
          {t('refresh')}
        </Button>
      </StyledBox>
    );
  }

  return (
    <LexicalComposer
      initialConfig={initialConfig}
    >
      <SharedHistoryContext>
        <TableContext>
          <div className="editor-shell">
            <InnerWysiwyg
              onChange={(value) => {
                try {
                  const jsonValue = JSON.parse(value);
                  if (!jsonValue?.root?.children?.length) {
                    setRefresh(true);
                    onChange(JSON.stringify(defaultRichText));
                  } else {
                    onChange(value);
                  }
                } catch (e) {
                  console.error(`Error parsing editor state: "${value}"`, e);
                  setRefresh(true);
                  onChange(JSON.stringify(defaultRichText));
                }
              }}
              {...others}
            />
          </div>
        </TableContext>
      </SharedHistoryContext>
    </LexicalComposer>
  );
}
